/**
 * EPIC-DM01-G: Demo Share Page (Redesigned)
 * Full page share experience with 5-platform card preview
 *
 * Route: /demo/share/:sessionId
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDemoMode } from '@/hooks/useDemoMode';
import { useDemoResults } from '@/hooks/useDemoResults';
import { useDemoShare } from '@/hooks/useDemoShare';
import { ShareCardPreview, type ShareFormat } from '@/components/demo/ShareCard';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Copy,
  Loader2,
  Share2,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import { DemoButton, DemoCard } from '@/components/demo/ui';

export default function DemoShare() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const {
    canAccessDemoMode,
    isDemoModeEnabled,
    isLoading: isLoadingConfig,
  } = useDemoMode();

  const {
    viewModel,
    shareEligible,
    isLoading: isLoadingResults,
    error: resultsError,
  } = useDemoResults(sessionId || '');

  const {
    isGenerating,
    isRevoking,
    shareUrl,
    previewData,
    error: shareError,
    errorReason,
    hasNativeShare,
    generateShareCard,
    revokeShare,
    copyShareUrl,
    nativeShare,
    clearError,
  } = useDemoShare(sessionId || '');

  const [format, setFormat] = useState<ShareFormat>('instagram-feed');
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);
  const [revoked, setRevoked] = useState(false);

  // Generate share card on mount
  useEffect(() => {
    if (sessionId && shareEligible && !shareUrl && !isGenerating && !shareError) {
      generateShareCard('1:1');
    }
  }, [sessionId, shareEligible, shareUrl, isGenerating, shareError, generateShareCard]);

  const handleFormatChange = (newFormat: ShareFormat) => {
    setFormat(newFormat);
    clearError();
    const legacyFormat = newFormat === 'instagram-story' ? '9:16' : '1:1';
    generateShareCard(legacyFormat);
  };

  const handleCopy = async () => {
    const success = await copyShareUrl();
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNativeShare = async () => {
    const success = await nativeShare();
    if (success) {
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    }
  };

  const handleRevoke = async () => {
    const result = await revokeShare();
    if (result.ok) {
      setRevoked(true);
    }
  };

  // ── Loading ────────────────────────────────────────────────────
  if (isLoadingConfig || isLoadingResults) {
    return (
      <div className="demo-mode min-h-screen bg-demo-surface flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-demo-green" />
          <p className="text-demo-text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  // ── Not authorized ─────────────────────────────────────────────
  if (!canAccessDemoMode || !isDemoModeEnabled) {
    return (
      <div className="demo-mode min-h-screen bg-demo-surface flex items-center justify-center p-4">
        <DemoCard className="max-w-md w-full">
          <div className="p-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-demo-error" />
            <h2 className="text-xl font-bold text-demo-text mb-2">Access Denied</h2>
            <p className="text-demo-text-muted mb-4">You do not have permission to access Demo Mode.</p>
            <DemoButton variant="outline" onClick={() => navigate('/home')}>Return to Home</DemoButton>
          </div>
        </DemoCard>
      </div>
    );
  }

  // ── Results error ──────────────────────────────────────────────
  if (resultsError) {
    return (
      <div className="demo-mode min-h-screen bg-demo-surface flex items-center justify-center p-4">
        <DemoCard className="max-w-md w-full">
          <div className="p-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-demo-error" />
            <h2 className="text-xl font-bold text-demo-text mb-2">Error Loading Results</h2>
            <p className="text-demo-text-muted mb-4">{resultsError.message || 'Unable to load session data'}</p>
            <DemoButton variant="outline" onClick={() => navigate(`/demo/results/${sessionId}`)}>
              Return to Results
            </DemoButton>
          </div>
        </DemoCard>
      </div>
    );
  }

  // ── Not eligible ───────────────────────────────────────────────
  if (!shareEligible) {
    return (
      <div className="demo-mode min-h-screen bg-demo-surface flex items-center justify-center p-4">
        <DemoCard className="max-w-md w-full">
          <div className="p-6 text-center">
            <ShieldAlert className="h-12 w-12 mx-auto mb-4 text-demo-gold" />
            <h2 className="text-xl font-bold text-demo-text mb-2">Sharing Not Available</h2>
            <p className="text-demo-text-muted mb-4">
              Social sharing was not enabled for this session. The prospect must opt-in during intake.
            </p>
            <DemoButton variant="outline" onClick={() => navigate(`/demo/results/${sessionId}`)}>
              Return to Results
            </DemoButton>
          </div>
        </DemoCard>
      </div>
    );
  }

  // Preview data
  const displayPreview = previewData || {
    format,
    insight: viewModel?.top2Insights?.[0]?.description || 'Your swing has been analyzed!',
    focusArea: viewModel?.focusArea?.title || 'Swing Improvement',
    overallScore: viewModel?.overallScore,
  };

  const isBlocked = shareError && (errorReason === 'user_not_opted_in' || errorReason === 'system_sharing_disabled');

  // ── Main share UI ──────────────────────────────────────────────
  return (
    <div className="demo-mode min-h-screen bg-demo-surface">
      {/* Sticky header */}
      <header className="sticky top-0 z-10 bg-white border-b border-gray-100 shadow-demo-sm">
        <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`/demo/results/${sessionId}`)}
            className="h-10 w-10 flex items-center justify-center rounded-xl text-demo-text-muted hover:bg-demo-surface-alt"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-demo-text flex items-center gap-2">
              <Share2 className="h-5 w-5 text-demo-green" />
              Share Your Analysis
            </h1>
            <p className="text-xs text-demo-text-muted">Choose a format and share</p>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-xl mx-auto px-4 py-6 pb-32">
        {/* Blocked state */}
        {isBlocked && (
          <DemoCard variant="warning" className="mb-6">
            <div className="flex items-start gap-3">
              <ShieldAlert className="h-5 w-5 text-demo-error shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-demo-error text-sm">Sharing Not Available</p>
                <p className="text-xs text-demo-error/80 mt-1">
                  {errorReason === 'user_not_opted_in'
                    ? 'Social sharing was not enabled for this session.'
                    : 'Social sharing is currently disabled.'}
                </p>
              </div>
            </div>
          </DemoCard>
        )}

        {/* Error (non-blocked) */}
        {shareError && !isBlocked && (
          <DemoCard variant="warning" className="mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-demo-error shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-demo-error text-sm">Error</p>
                <p className="text-xs text-demo-error/80 mt-1">{shareError}</p>
              </div>
            </div>
            <DemoButton
              variant="outline"
              size="sm"
              fullWidth
              className="mt-3"
              onClick={() => {
                clearError();
                generateShareCard('1:1');
              }}
            >
              Try Again
            </DemoButton>
          </DemoCard>
        )}

        {/* Revoked state */}
        {revoked && (
          <DemoCard className="mb-6">
            <div className="flex items-center gap-3 text-center justify-center py-2">
              <Check className="h-5 w-5 text-demo-green" />
              <p className="font-medium text-demo-green-dark">All share links revoked</p>
            </div>
          </DemoCard>
        )}

        {/* Generating spinner */}
        {isGenerating && (
          <div className="flex flex-col items-center gap-4 py-12">
            <Loader2 className="h-10 w-10 animate-spin text-demo-green" />
            <p className="text-demo-text-muted">Generating share card...</p>
          </div>
        )}

        {/* Preview + actions */}
        {!isGenerating && !isBlocked && !revoked && (
          <div className="flex flex-col items-center gap-6">
            {/* Card preview with 5-format selector */}
            <ShareCardPreview
              format={format}
              insight={displayPreview.insight}
              focusArea={displayPreview.focusArea}
              overallScore={displayPreview.overallScore}
              onFormatChange={handleFormatChange}
            />

            {/* Share URL */}
            {shareUrl && (
              <div className="w-full">
                <label className="text-xs font-medium text-demo-text-muted mb-2 block">Share Link</label>
                <div className="flex items-center gap-2 p-3 bg-demo-surface-alt rounded-xl">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 bg-transparent text-sm truncate outline-none text-demo-text"
                  />
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-white"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-demo-green" />
                    ) : (
                      <Copy className="h-4 w-4 text-demo-text-muted" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col gap-3 w-full">
              <DemoButton
                variant="primary"
                size="lg"
                fullWidth
                onClick={handleCopy}
                disabled={!shareUrl || copied}
              >
                {copied ? (
                  <>
                    <Check className="mr-2 h-5 w-5" />
                    Link Copied!
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-5 w-5" />
                    Copy Link
                  </>
                )}
              </DemoButton>

              {hasNativeShare && (
                <DemoButton
                  variant="secondary"
                  size="lg"
                  fullWidth
                  onClick={handleNativeShare}
                  disabled={!shareUrl || shared}
                >
                  {shared ? (
                    <>
                      <Check className="mr-2 h-5 w-5" />
                      Shared!
                    </>
                  ) : (
                    <>
                      <Share2 className="mr-2 h-5 w-5" />
                      Share
                    </>
                  )}
                </DemoButton>
              )}

              {/* Revoke */}
              {shareUrl && (
                <DemoButton
                  variant="ghost"
                  size="sm"
                  onClick={handleRevoke}
                  loading={isRevoking}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Revoke All Share Links
                </DemoButton>
              )}
            </div>

            {/* Privacy notice */}
            <p className="text-xs text-demo-text-muted text-center max-w-sm">
              Share links expire after 7 days. No personal information is included in shared content.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
